/**
 * NAID compliance overview — all employees' screening/test status + Key Fobs.
 * Admin can edit; Managers can view.
 */
import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ShieldCheck, Pencil, KeyRound, FileText, Upload, ExternalLink } from 'lucide-react';
import {
  useNaidOverview,
  useUpdateNaid,
  useAssignAsset,
  useReturnAsset,
  useUploadNaidDocument,
  naidDocumentContentUrl,
  type NaidDocKind,
} from '../api.js';
import { Table, type Column } from '../../../shell/Table.js';
import { Badge } from '../../../shell/Badge.js';
import { Button } from '../../../shell/Button.js';
import { Modal } from '../../../shell/Modal.js';
import { Input, Select, Field, Textarea } from '../../../shell/Field.js';
import { EmptyState } from '../../../shell/EmptyState.js';
import { formatDate } from '../../../lib/format.js';
import { toast } from '../../../shell/Toast.js';
import { ApiRequestError } from '../../../lib/api.js';
import { useAuthStore } from '../../../store/auth.js';
import type { NaidOverviewRow, NaidComplianceStatus, I9Status, NaidStoredDocument } from '@shared/types';
import { I9_STATUS } from '@shared/enums/index.js';

type Filter =
  | 'all'
  | 'overdue'
  | 'dueSoon'
  | 'remote'
  | 'missingFob'
  | 'incomplete';

const statusTone: Record<NaidComplianceStatus, 'green' | 'yellow' | 'red' | 'slate' | 'blue'> = {
  Compliant: 'green',
  DueSoon: 'yellow',
  Overdue: 'red',
  Incomplete: 'blue',
  NotApplicable: 'slate',
};

function toDateInput(iso: string | null | undefined): string {
  if (!iso) return '';
  return iso.slice(0, 10);
}

const NAID_DOC_ACCEPT =
  '.pdf,.doc,.docx,image/jpeg,image/png,image/webp,image/gif,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function NaidDocumentField({
  label,
  employeeId,
  kind,
  document,
}: {
  label: string;
  employeeId: string;
  kind: NaidDocKind;
  document: NaidStoredDocument | null | undefined;
}) {
  const upload = useUploadNaidDocument();
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast('error', 'File must be 10 MB or smaller.');
      return;
    }
    try {
      await upload.mutateAsync({ employeeId, kind, file });
      toast('success', `${label} uploaded.`);
    } catch (err) {
      toast(
        'error',
        err instanceof ApiRequestError ? err.body.message : `Failed to upload ${label.toLowerCase()}.`,
      );
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <Field label={label}>
      <div className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700">
        {document ? (
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-900 dark:text-slate-100">
                <FileText className="h-4 w-4 shrink-0 text-slate-400" />
                <span className="truncate">{document.name}</span>
              </div>
              <div className="mt-1 text-xs text-slate-500">
                {formatBytes(document.sizeBytes)} · uploaded {formatDate(document.uploadedAt)}
              </div>
            </div>
            <div className="flex shrink-0 gap-2">
              <a
                href={naidDocumentContentUrl(employeeId, kind)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Open
              </a>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => fileRef.current?.click()}
                loading={upload.isPending}
              >
                Replace
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm text-slate-500">No file uploaded yet.</p>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => fileRef.current?.click()}
              loading={upload.isPending}
            >
              <Upload className="h-3.5 w-3.5" />
              Upload
            </Button>
          </div>
        )}
        <input
          ref={fileRef}
          type="file"
          accept={NAID_DOC_ACCEPT}
          className="hidden"
          onChange={(e) => void handleFile(e.target.files?.[0])}
        />
      </div>
    </Field>
  );
}

function EditNaidModal({
  row,
  onClose,
}: {
  row: NaidOverviewRow;
  onClose: () => void;
}) {
  const update = useUpdateNaid();
  const assign = useAssignAsset();
  const returnAsset = useReturnAsset();
  const naid = row.naid;

  const [drug, setDrug] = useState(toDateInput(naid?.drugScreeningOn));
  const [background, setBackground] = useState(toDateInput(naid?.backgroundOn));
  const [yearly, setYearly] = useState(toDateInput(naid?.yearlyTestOn));
  const [ndaYear, setNdaYear] = useState(naid?.ndaYear?.toString() ?? '');
  const [i9, setI9] = useState<I9Status>(naid?.i9Status ?? 'Pending');
  const [result, setResult] = useState(naid?.yearlyTestResult ?? '');
  const [accessRequired, setAccessRequired] = useState(
    naid?.accessRequired ?? row.workLocation === 'OnSite',
  );
  const [incompleteNotificationsExempt, setIncompleteNotificationsExempt] = useState(
    naid?.incompleteNotificationsExempt ?? false,
  );
  const [notes, setNotes] = useState(naid?.notes ?? '');
  const [fobSerial, setFobSerial] = useState(row.keyFob?.serialNumber ?? '');

  const handleSave = async () => {
    try {
      await update.mutateAsync({
        employeeId: row.employeeId,
        body: {
          drugScreeningOn: drug || null,
          backgroundOn: background || null,
          yearlyTestOn: yearly || null,
          ndaYear: ndaYear ? Number(ndaYear) : null,
          i9Status: i9,
          yearlyTestResult: result || null,
          accessRequired,
          incompleteNotificationsExempt,
          notes: notes || null,
        },
      });

      const serial = fobSerial.trim();
      if (accessRequired && serial && serial !== row.keyFob?.serialNumber) {
        if (row.keyFob) {
          await returnAsset.mutateAsync({ id: row.keyFob.id });
        }
        await assign.mutateAsync({
          employeeId: row.employeeId,
          assetType: 'KeyFob',
          serialNumber: serial,
        });
      }

      toast('success', 'NAID record updated.');
      onClose();
    } catch (err) {
      toast(
        'error',
        err instanceof ApiRequestError ? err.body.message : 'Failed to update NAID.',
      );
    }
  };

  const pending = update.isPending || assign.isPending || returnAsset.isPending;

  return (
    <Modal open onClose={onClose} title={`NAID — ${row.name}`} size="lg">
      <div className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="Drug screening" type="date" value={drug} onChange={(e) => setDrug(e.target.value)} />
          <Input
            label="Background check"
            type="date"
            value={background}
            onChange={(e) => setBackground(e.target.value)}
          />
          <Input
            label="NDA year"
            type="number"
            value={ndaYear}
            onChange={(e) => setNdaYear(e.target.value)}
            placeholder="2026"
          />
          <Select
            label="I-9"
            value={i9}
            onChange={(e) => setI9(e.target.value as I9Status)}
          >
            {I9_STATUS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Select>
        </div>

        <Field label="Access employee (yearly test required)">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={accessRequired}
              onChange={(e) => setAccessRequired(e.target.checked)}
            />
            Access / On-site employee
          </label>
        </Field>

        <Field label="Incomplete reminder emails">
          <label className="flex items-start gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={incompleteNotificationsExempt}
              onChange={(e) => setIncompleteNotificationsExempt(e.target.checked)}
            />
            <span>
              Exempt from incomplete NAID emails
              <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                Employee stays on the NAID panel; Mon/Fri incomplete reminders are not sent.
              </span>
            </span>
          </label>
        </Field>

        {accessRequired && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="Yearly access test date"
              type="date"
              value={yearly}
              onChange={(e) => setYearly(e.target.value)}
            />
            <Input
              label="Test result / code"
              value={result}
              onChange={(e) => setResult(e.target.value)}
              placeholder="100% NF-013A"
            />
            <Input
              label="Key Fob serial"
              value={fobSerial}
              onChange={(e) => setFobSerial(e.target.value)}
              placeholder="8616306"
            />
          </div>
        )}

        <NaidDocumentField
          label="Signed NDA"
          employeeId={row.employeeId}
          kind="nda"
          document={naid?.ndaDocument}
        />

        {accessRequired && (
          <NaidDocumentField
            label="Completed yearly access test"
            employeeId={row.employeeId}
            kind="yearly-test"
            document={naid?.yearlyTestDocument}
          />
        )}

        <Textarea label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={pending}>Save</Button>
        </div>
      </div>
    </Modal>
  );
}

export function NaidPage() {
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'Admin';
  const { data, isLoading } = useNaidOverview();
  const [filter, setFilter] = useState<Filter>('all');
  const [editRow, setEditRow] = useState<NaidOverviewRow | null>(null);

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    switch (filter) {
      case 'overdue':
        return items.filter((r) => r.naid?.complianceStatus === 'Overdue');
      case 'dueSoon':
        return items.filter((r) => r.naid?.complianceStatus === 'DueSoon');
      case 'remote':
        return items.filter((r) => r.workLocation === 'Remote');
      case 'missingFob':
        return items.filter((r) => r.missingKeyFob);
      case 'incomplete':
        return items.filter((r) => !r.naid || r.naid.complianceStatus === 'Incomplete');
      default:
        return items;
    }
  }, [data?.items, filter]);

  const columns: Column<NaidOverviewRow>[] = [
    {
      key: 'emp',
      header: 'Employee',
      render: (r) => (
        <div>
          <Link
            to={`/employees/${r.employeeId}`}
            className="font-medium text-slate-900 hover:underline dark:text-slate-100"
          >
            {r.name}
          </Link>
          <div className="text-xs text-slate-400">
            {r.employeeNumber ?? '—'} · {r.department}
          </div>
        </div>
      ),
    },
    {
      key: 'loc',
      header: 'Location',
      render: (r) => (
        <Badge tone={r.workLocation === 'Remote' ? 'slate' : 'blue'}>{r.workLocation}</Badge>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const s = r.naid?.complianceStatus ?? 'Incomplete';
        return (
          <div className="flex flex-wrap items-center gap-1">
            <Badge tone={statusTone[s]}>{s}</Badge>
            {r.naid?.incompleteNotificationsExempt && (
              <Badge tone="slate">Nag exempt</Badge>
            )}
          </div>
        );
      },
    },
    {
      key: 'drug',
      header: 'Drug',
      render: (r) => <span className="text-sm">{formatDate(r.naid?.drugScreeningOn)}</span>,
    },
    {
      key: 'bg',
      header: 'Background / Due',
      render: (r) => (
        <div className="text-sm">
          <div>{formatDate(r.naid?.backgroundOn)}</div>
          <div className="text-xs text-slate-400">Due {formatDate(r.naid?.backgroundDue)}</div>
        </div>
      ),
    },
    {
      key: 'nda',
      header: 'NDA',
      render: (r) => <span className="text-sm">{r.naid?.ndaYear ?? '—'}</span>,
    },
    {
      key: 'i9',
      header: 'I-9',
      render: (r) => <span className="text-sm">{r.naid?.i9Status ?? '—'}</span>,
    },
    {
      key: 'test',
      header: 'Yearly Test / Due',
      render: (r) =>
        r.naid?.accessRequired === false ? (
          <span className="text-sm text-slate-400">N/A</span>
        ) : (
          <div className="text-sm">
            <div>{r.naid?.yearlyTestResult || formatDate(r.naid?.yearlyTestOn)}</div>
            <div className="text-xs text-slate-400">Due {formatDate(r.naid?.yearlyTestDue)}</div>
          </div>
        ),
    },
    {
      key: 'fob',
      header: 'Key Fob',
      render: (r) =>
        r.keyFob ? (
          <span className="inline-flex items-center gap-1 text-sm">
            <KeyRound className="h-3.5 w-3.5 text-slate-400" />
            {r.keyFob.serialNumber}
          </span>
        ) : r.missingKeyFob ? (
          <Badge tone="yellow">Missing</Badge>
        ) : (
          <span className="text-sm text-slate-400">—</span>
        ),
    },
    ...(isAdmin
      ? [
          {
            key: 'actions',
            header: '',
            render: (r: NaidOverviewRow) => (
              <Button variant="ghost" size="sm" onClick={() => setEditRow(r)}>
                <Pencil className="h-4 w-4" />
              </Button>
            ),
          } satisfies Column<NaidOverviewRow>,
        ]
      : []),
  ];

  const filters: { id: Filter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'overdue', label: 'Overdue' },
    { id: 'dueSoon', label: 'Due soon' },
    { id: 'incomplete', label: 'Incomplete' },
    { id: 'missingFob', label: 'Missing fob' },
    { id: 'remote', label: 'Remote' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-900 dark:text-slate-100">
            <ShieldCheck className="h-6 w-6 text-slate-500" />
            NAID Compliance
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Drug screening, background (+36 mo), NDA, I-9, yearly access test (+12 mo), and Key Fobs.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`rounded-md px-3 py-1.5 text-sm ${
              filter === f.id
                ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<ShieldCheck className="h-8 w-8" />}
          title="No matching employees"
          description="Try a different filter or add employees with NAID records."
        />
      ) : (
        <Table columns={columns} data={filtered} />
      )}

      {editRow && <EditNaidModal row={editRow} onClose={() => setEditRow(null)} />}
    </div>
  );
}
